import { Button, Typography } from '@breetometer/components';
import { IdentityVerification, RecoveryCodesExhaustedIcon } from 'BreetAssets';
import { DialogAccessCredentials, Loader, ModalParts, RecoveryCodeForm } from 'BreetComponents';
import {
	alwaysRefetchApiQueryOptions,
	handleApiError,
	HTTPSTATUSCODE,
	type IUseAccessCredentialsRequest,
	OTPLENGTH,
	showToast,
	type TwoFaType,
} from 'BreetHelpers';
import { useAccessCredentials, useFormStage, useMfaTransactionsApi, useModalApi } from 'BreetHooks';
import { useGetUserPreferenceQuery, useLazyVerifyPinQuery } from 'BreetRedux';
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type VERIFYTRANSACTIONSTAGETYPE, verifyTransactionTitle } from './DialogVerifyTransaction.helpers';

const DialogVerifyTransaction = () => {
	const { fetchOTP, isApiloading, skipPin, transactionType, closeModal } = useMfaTransactionsApi();

	const { data: userPreferenceData, isLoading: userPreferenceLoading } = useGetUserPreferenceQuery(
		undefined,
		alwaysRefetchApiQueryOptions
	);

	const [verifyPinFn] = useLazyVerifyPinQuery();

	const [pin, setPin] = useState<string>('');

	const [recoveryCodeValue, setRecoveryCodeValue] = useState<string>('');

	const { closeModalDirect, handleOpenModal, openModalDirect } = useModalApi();

	const formStage = useFormStage<VERIFYTRANSACTIONSTAGETYPE>(
		skipPin ? 'otp-verification' : 'pin-verification',
		['pin-verification', 'otp-verification', 'recovery-code', 'exhausted-recovery-code'],
		verifyTransactionTitle,
		'verifyTransaction'
	);

	const skipPinAndMfa = useMemo(
		() => transactionType === 'convertFiatToUSD' || transactionType === 'sell-usd',
		[transactionType]
	);

	const isMfa = useMemo(
		() => !!userPreferenceData?.data?.isAuthenticator2FaActive || !!userPreferenceData?.data?.is2FAActive,
		[userPreferenceData]
	);

	const skipMfa = useMemo(() => transactionType === 'swapCurrency' || !isMfa, [isMfa, transactionType]);

	const submitRequest = useCallback(
		(data?: IUseAccessCredentialsRequest) => {
			if (!data?.code) {
				showToast('error', 'Something went wrong');
				return;
			}

			const defaultType = !userPreferenceData?.data?.isAuthenticator2FaActive ? 'email' : 'authenticator';

			const otpProps: TwoFaType = {
				code: data.code,
				type: formStage.isStageActive('recovery-code') ? 'recoveryCode' : defaultType,
			};

			fetchOTP(pin, otpProps)
				?.then(() => {
					setPin('');
					data.onSuccess?.();
					closeModal();
				})
				.catch((e: unknown) => {
					data.onError(e);
				});
		},
		[fetchOTP, formStage, closeModal, pin, userPreferenceData?.data?.isAuthenticator2FaActive]
	);

	const refectchOTP = useCallback(async () => {
		await fetchOTP(pin)?.catch(handleApiError);
	}, [fetchOTP, pin]);

	const description = !userPreferenceData?.data?.isAuthenticator2FaActive
		? `Enter the ${OTPLENGTH}-digit code sent to your <span class='color-tertiary-400'>email inbox</span> to continue`
		: `Enter the ${OTPLENGTH}-digit code from your <span class='color-tertiary-400'>authenticator app</span> to continue`;

	const handleRecoveryCodeValue = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setRecoveryCodeValue(e.target.value);
	}, []);

	const isExhaustedRecoveryCode = userPreferenceData?.data?.usedRecoveryCodesCount === 10;

	const submitRecoveryCode = useCallback(() => {
		if (isExhaustedRecoveryCode) {
			formStage.navigator.goTo('exhausted-recovery-code')();
			return;
		}

		submitRequest({
			code: recoveryCodeValue,
			onError: () => {
				setRecoveryCodeValue('');
			},
		});
	}, [recoveryCodeValue, submitRequest, formStage, isExhaustedRecoveryCode]);

	const { component: otpComponent } = useAccessCredentials({
		id: '2fa-step',
		mode: 'otp',
		submitRequest,
		dialogMessage: 'Verifying OTP...',
		title: 'Verify Your Identity',
		description,
		type: 'collect',
		persistOnVerify: true,
		generateOTP: false,
		resendOtp: userPreferenceData?.data?.is2FAActive,
		render: (props) => (
			<DialogAccessCredentials
				{...props}
				refetchFn={refectchOTP}
				otpCta={
					!userPreferenceData?.data?.isAuthenticator2FaActive
						? undefined
						: {
								btnTitle: 'Use Recovery Code',
								message: "Can't find code?",
								action: formStage.navigator.goTo(isExhaustedRecoveryCode ? 'exhausted-recovery-code' : 'recovery-code'),
							}
				}
			/>
		),
	});

	const requestEmailOTP = useCallback(
		(data?: IUseAccessCredentialsRequest) => {
			if (!data?.code) {
				showToast('error', 'Something went wrong');
				return;
			}

			fetchOTP(data.code)?.catch((e: unknown) => {
				closeModalDirect('loadingDialog');

				const error = e as { data: { message: string }; status: number };

				if (error.status === HTTPSTATUSCODE.TRANSACTIONNOAUTH && data.code) {
					setPin(data.code);

					formStage.navigator.goTo('otp-verification')();
				} else {
					data.onError(e);
				}
			});
		},
		[closeModalDirect, fetchOTP, formStage.navigator]
	);

	const requestAuthenticatorOtp = useCallback(
		(pinCodeArg: string) => {
			setPin(pinCodeArg);

			formStage.navigator.goTo('otp-verification')();
		},
		[formStage.navigator]
	);

	const onVerifyPINSuccess = useCallback(
		(data: IUseAccessCredentialsRequest) => {
			closeModalDirect('loadingDialog');

			if (!data.code) {
				return;
			}

			if (skipMfa) {
				fetchOTP(data.code)
					?.then(() => {
						data.onSuccess?.();
						if (transactionType === 'saveWalletAddress') {
							closeModalDirect('verifyTransaction');
							return;
						}

						closeModal();
					})
					.catch((e: unknown) => {
						data.onError(e);
					});
				return;
			}

			if (userPreferenceData?.data?.isAuthenticator2FaActive) {
				requestAuthenticatorOtp(data.code);
			} else {
				requestEmailOTP(data);
			}
		},
		[
			closeModalDirect,
			fetchOTP,
			closeModal,
			requestAuthenticatorOtp,
			requestEmailOTP,
			skipMfa,
			userPreferenceData?.data?.isAuthenticator2FaActive,
			transactionType,
		]
	);

	const pinVerification = useCallback(
		(data?: IUseAccessCredentialsRequest) => {
			if (!data?.code) {
				return;
			}

			verifyPinFn({ pin: data.code })
				.unwrap()
				.then(() => {
					onVerifyPINSuccess(data);
				})
				.catch((e: unknown) => {
					closeModalDirect('loadingDialog');
					handleApiError(e);
				});
		},
		[closeModalDirect, onVerifyPINSuccess, verifyPinFn]
	);

	const { component: pinComponent } = useAccessCredentials({
		id: 'withdraw-request',
		mode: 'pin',
		submitRequest: pinVerification,
		dialogMessage: 'Processing request...',
		type: 'collect',
		render: (props) => <DialogAccessCredentials {...props} />,
	});

	const callOnceRef = useRef<boolean>(false);

	const isAutoFetchOtp = useMemo(
		() => skipPinAndMfa || !!(skipPin && !isMfa) || !!(skipPin && !!userPreferenceData?.data?.is2FAActive),
		[skipPinAndMfa, skipPin, isMfa, userPreferenceData]
	);

	useEffect(() => {
		if (isAutoFetchOtp && !callOnceRef.current) {
			callOnceRef.current = true;

			openModalDirect({ id: 'loadingDialog', metadata: { message: 'Processing request...' } });

			fetchOTP('')
				?.then(() => {
					closeModal();
				})
				.catch((e: unknown) => {
					closeModalDirect('loadingDialog');
					handleApiError(e);
				});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userPreferenceData]);

	if (userPreferenceLoading) return <Loader />;

	if (skipPinAndMfa) return null;

	return (
		<ModalParts.Wrapper
			title={formStage.stageTitle}
			rightSheet
			root={verifyTransactionTitle[skipPin ? 'otp-verification' : 'pin-verification']}
			handleBackLink={formStage.navigator.historyBack}
		>
			<ModalParts.Stage hide={!formStage.isStageActive('pin-verification')}>{pinComponent}</ModalParts.Stage>
			<ModalParts.Stage hide={!formStage.isStageActive('otp-verification')}>
				<div className='verify-transaction-otp'>
					<IdentityVerification />
					{otpComponent}
				</div>
			</ModalParts.Stage>
			<ModalParts.Stage hide={!formStage.isStageActive('recovery-code')}>
				<RecoveryCodeForm
					handleRecoveryCodeValue={handleRecoveryCodeValue}
					btnDisabled={recoveryCodeValue.length < 10}
					recoveryCodeValue={recoveryCodeValue}
					submitRecoveryCode={submitRecoveryCode}
					isLoading={isApiloading}
					userPreferenceData={userPreferenceData}
				/>
			</ModalParts.Stage>
			<ModalParts.Stage hide={!formStage.isStageActive('exhausted-recovery-code')}>
				<div className='exhausted-recovery-code'>
					<RecoveryCodesExhaustedIcon />

					<Typography
						as='h5'
						className='color-tertiary-400'
					>
						Recovery Code Exhausted
					</Typography>
					<Typography className='color-primary-100'>
						You&apos;ve used all available recovery codes. Contact support to regain access to reset.
					</Typography>

					<Button onClick={handleOpenModal({ id: 'contactSupport' })}>Contact Support</Button>
				</div>
			</ModalParts.Stage>
		</ModalParts.Wrapper>
	);
};

export default DialogVerifyTransaction;
